import React, { useState } from 'react';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import { faThumbsDown } from '@fortawesome/free-solid-svg-icons/faThumbsDown';
import { faThumbsUp } from '@fortawesome/free-solid-svg-icons/faThumbsUp';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';

import {
  CreateUserFeedbackInput,
  useCreateUserFeedbackMutation,
  useUserFeedbackSubmittedQuery
} from 'interfaces/graphql-typings';

import styles from './sideNavigationFeedbackStyles.scss';

const UserFeedbackSubmitted = require('buyside/graphql/queries/UserFeedbackSubmitted.graphql');

const { userFeedbackPrompts } = require('buyside/util/constantsFromRails.json');

export const activePrompt = 'home_page';
export const promptMessage = userFeedbackPrompts[activePrompt];
export const successMessage =
  'Thank you for your feedback! Your opinion is important to us and we appreciate you taking the time to share your thoughts.';

const SideNavigationFeedback: React.FC<{ currentUserId: string }> = ({ currentUserId }) => {
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const {
    data,
    loading: feedbackSubmittedLoading,
    error: feedbackSubmittedError
  } = useUserFeedbackSubmittedQuery({ variables: { prompt: activePrompt } });

  const [
    createFeedback,
    { loading: createFeedbackLoading, error: createFeedbackError }
  ] = useCreateUserFeedbackMutation({
    onCompleted: data =>
      Boolean(data.createUserFeedback?.userFeedback?.sentiment) && setFeedbackSubmitted(true),
    update: cache =>
      cache.writeQuery({
        query: UserFeedbackSubmitted,
        data: {
          currentUser: {
            __typename: 'User',
            id: currentUserId,
            userFeedbackSubmitted: true
          }
        },
        variables: { prompt: activePrompt }
      })
  });

  if (feedbackSubmittedError) throw feedbackSubmittedError;
  if (createFeedbackError) throw createFeedbackError;

  const {
    register,
    handleSubmit,
    watch,
    formState: { isDirty },
    setValue,
    reset
  } = useForm<CreateUserFeedbackInput>({
    defaultValues: {
      comment: null,
      prompt: activePrompt,
      rating: 0
    }
  });

  const onSubmit = handleSubmit(variables => createFeedback({ variables }).then(() => reset()));

  return feedbackSubmitted ? (
    <div className={styles.sideNavigationFeedback}>
      <div className={styles.feedbackSubmitted}>{successMessage}</div>
    </div>
  ) : (
    <>
      {Boolean(data?.currentUser?.userFeedbackSubmitted) || feedbackSubmittedLoading ? null : (
        <div className={styles.sideNavigationFeedback}>
          <form className={styles.feedbackArea} onSubmit={onSubmit}>
            {isDirty ? (
              <textarea
                {...register('comment', { required: false })}
                rows={4}
                placeholder={promptMessage}
              />
            ) : (
              <p>{promptMessage}</p>
            )}
            <div className={styles.ratingButtons}>
              <Button
                buttonType={BUTTON_TYPES.BUTTON}
                className={classNames({ [styles.selected]: watch('rating') === 1 })}
                disabled={createFeedbackLoading}
                faIcon={faThumbsUp}
                onClick={(): void => setValue('rating', 1, { shouldDirty: true })}
                size={BUTTON_SIZES.TINY}
                title="positiveFeedback"
              />
              <Button
                buttonType={BUTTON_TYPES.BUTTON}
                className={classNames({ [styles.selected]: watch('rating') === -1 })}
                disabled={createFeedbackLoading}
                faIcon={faThumbsDown}
                onClick={(): void => setValue('rating', -1, { shouldDirty: true })}
                size={BUTTON_SIZES.TINY}
                title="negativeFeedback"
              />
            </div>
            <Button
              buttonType={BUTTON_TYPES.BUTTON}
              className={styles.dismissButton}
              disabled={createFeedbackLoading}
              onClick={(): void => {
                setValue('comment', null);
                setValue('rating', 0);
                onSubmit();
              }}
              size={BUTTON_SIZES.TINY}
              text="Dismiss"
              title="dismissFeedback"
            />
            {isDirty && (
              <Button
                isLoading={createFeedbackLoading}
                size={BUTTON_SIZES.TINY}
                text="Submit Feedback"
                type={BUTTON_TYPES.PRIMARY}
              />
            )}
            <Button
              buttonType={BUTTON_TYPES.BUTTON}
              className={styles.legacyButton}
              onClick={() => window.location.assign(`/pro?${window.btoa('old home page')}`)}
              size={BUTTON_SIZES.TINY}
              text="Go to Old Home"
            />
          </form>
        </div>
      )}
    </>
  );
};

export default SideNavigationFeedback;
